import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { PlanCardShell, PlanMetricRow } from '../components/PlanCardShell';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService, { SIGNUP_ROLES_FOR_LAYER_DEFAULTS, type RoleComputePresetRow } from '../services/adminService';
import type { User } from '../types/auth';

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

const ROLE_TABS: readonly { value: (typeof SIGNUP_ROLES_FOR_LAYER_DEFAULTS)[number]; label: string }[] = [
  { value: 'blo', label: 'BLO' },
  { value: 'plantation_owner', label: 'Plantation owner' },
  { value: 'lgu', label: 'LGU' },
  { value: 'project_developer', label: 'Carbon Project Developer' },
] as const;

type Draft = Omit<RoleComputePresetRow, 'role_key' | 'updated_at'>;

function EditableMetricRow({ label, suffix, children }: { label: string; suffix: string; children: ReactNode }) {
  return (
    <PlanMetricRow
      label={label}
      suffix={suffix}
      middleClassName="transition-shadow focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(16,185,129,0.15)] focus-within:ring-emerald-400/80"
    >
      {children}
    </PlanMetricRow>
  );
}

const inputNum =
  'w-full min-w-0 border-0 bg-transparent text-center text-sm font-semibold tabular-nums text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-0';

export default function AdminRoleComputePage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [activeRole, setActiveRole] = useState<(typeof SIGNUP_ROLES_FOR_LAYER_DEFAULTS)[number]>('blo');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [savingRole, setSavingRole] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const data = await adminService.listRoleComputePresets();
      const d: Record<string, Draft> = {};
      for (const r of data) {
        d[r.role_key] = {
          daily_hours: r.daily_hours,
          monthly_pack_hours: r.monthly_pack_hours,
          max_monthly_extra_hours: r.max_monthly_extra_hours,
          price_base_usd: r.price_base_usd,
          price_pack_usd: r.price_pack_usd,
          price_extra_per_hour_usd: r.price_extra_per_hour_usd,
          price_unlimited_usd: r.price_unlimited_usd,
        };
      }
      setDrafts(d);
    } catch (e: unknown) {
      setErr(
        String(
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            (e as Error).message ||
            'Failed to load'
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void load();
  }, [authLoading, me, load]);

  useEffect(() => {
    if (authLoading) return;
    if (me && !isAdminUser(me)) navigate('/', { replace: true });
  }, [authLoading, me, navigate]);

  const saveRole = async (roleKey: string) => {
    const body = drafts[roleKey];
    if (!body) return;
    setSavingRole(roleKey);
    setErr('');
    try {
      const updated = await adminService.updateRoleComputePreset(roleKey, body);
      setDrafts((prev) => ({
        ...prev,
        [roleKey]: {
          daily_hours: updated.daily_hours,
          monthly_pack_hours: updated.monthly_pack_hours,
          max_monthly_extra_hours: updated.max_monthly_extra_hours,
          price_base_usd: updated.price_base_usd,
          price_pack_usd: updated.price_pack_usd,
          price_extra_per_hour_usd: updated.price_extra_per_hour_usd,
          price_unlimited_usd: updated.price_unlimited_usd,
        },
      }));
    } catch (e: unknown) {
      setErr(
        String(
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            (e as Error).message ||
            'Save failed'
        )
      );
    } finally {
      setSavingRole(null);
    }
  };

  const d = drafts[activeRole];
  const tabLabel = ROLE_TABS.find((t) => t.value === activeRole)?.label ?? activeRole;

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Role analysis rules">
      <div className="max-w-6xl mx-auto space-y-5 pb-8">
        {err && (
          <div className="p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">{err}</div>
        )}

        <div className="overflow-hidden rounded-2xl bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_32px_-8px_rgba(0,0,0,0.1)] ring-1 ring-gray-950/[0.05]">
          <div className="border-b border-gray-100/90 bg-gradient-to-b from-gray-50/95 to-white px-4 py-5 sm:px-8">
            <div
              className="flex flex-wrap gap-2 justify-center sm:justify-start"
              role="tablist"
              aria-label="Signup segment"
            >
              {ROLE_TABS.map((tab) => {
                const active = activeRole === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveRole(tab.value)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                      active
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/25 ring-1 ring-emerald-600/20'
                        : 'bg-white/90 text-gray-600 shadow-sm ring-1 ring-gray-200/80 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <details
              open
              className="group mt-5 rounded-2xl border-0 bg-emerald-50/50 px-4 py-3 shadow-inner ring-1 ring-emerald-100/80"
            >
              <summary className="flex cursor-pointer list-none select-none items-center gap-2 text-sm font-semibold text-emerald-900 [&::-webkit-details-marker]:hidden">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100">
                  <i className="ri-information-line text-lg" aria-hidden />
                </span>
                How presets are used
                <i
                  className="ri-arrow-down-s-line ml-auto text-emerald-700/60 transition-transform group-open:rotate-180 sm:ml-0"
                  aria-hidden
                />
              </summary>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-xs leading-relaxed text-gray-700">
                <li>
                  <strong className="text-gray-900">Analysis time</strong> below sets real tool-time caps (daily vs monthly
                  pack vs unlimited). <strong className="text-gray-900">Unlimited</strong> has no monthly hour cap in the app.
                </li>
                <li>
                  <strong className="text-gray-900">Max add-on</strong> (monthly card) caps optional extra hours on monthly
                  plans only.
                </li>
                <li>
                  <strong className="text-gray-900">Reference prices</strong> are profile copy (USD) — not live billing.
                </li>
                <li>
                  <strong className="text-gray-900">Sessions:</strong> daily/monthly = one active login; unlimited = up to
                  User/staff use <strong className="text-gray-900">BLO</strong> rules.
                </li>
              </ul>
            </details>

            <p className="mt-4 text-xs text-gray-500">
              Editing rules for <span className="font-medium text-gray-800">{tabLabel}</span>.
            </p>
          </div>

          {loading || !d ? (
            <div className="p-12 text-center text-gray-500 text-sm">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 bg-gradient-to-b from-gray-50/90 to-white p-5 sm:gap-6 sm:p-8 lg:grid-cols-3">
                {/* Daily */}
                <PlanCardShell gradientClass="from-emerald-400 via-emerald-500 to-teal-500">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 ring-1 ring-emerald-100/90 shadow-sm">
                    <i className="ri-bolt-fill text-[13px]" aria-hidden /> Daily
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">Daily mode</h3>
                    <p className="mt-1 text-xs text-gray-500">Per UTC day allowance</p>
                  </div>
                  <EditableMetricRow label="List price" suffix="/ mo">
                    <span className="shrink-0 text-sm text-gray-400">$</span>
                    <input
                      id={`preset-${activeRole}-p-base`}
                      type="number"
                      min={0}
                      max={1_000_000}
                      step={0.01}
                      className={inputNum}
                      value={d.price_base_usd}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [activeRole]: {
                            ...prev[activeRole],
                            price_base_usd: parseFloat(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </EditableMetricRow>
                  <div className="mt-auto space-y-2 pt-2">
                    <EditableMetricRow label="Hours per UTC day" suffix="h/day">
                      <input
                        id={`preset-${activeRole}-daily`}
                        type="number"
                        min={0.01}
                        max={168}
                        step={0.25}
                        className={inputNum}
                        value={d.daily_hours}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [activeRole]: {
                              ...prev[activeRole],
                              daily_hours: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                      />
                    </EditableMetricRow>
                  </div>
                </PlanCardShell>

                {/* Monthly */}
                <PlanCardShell gradientClass="from-sky-400 via-blue-500 to-indigo-500">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-900 ring-1 ring-blue-100 shadow-sm">
                    <i className="ri-inbox-archive-line text-[13px]" aria-hidden /> Monthly
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">Monthly pack</h3>
                    <p className="mt-1 text-xs text-gray-500">Included hours + add-ons</p>
                  </div>
                  <EditableMetricRow label="Pack fee" suffix="/ mo">
                    <span className="shrink-0 text-sm text-gray-400">$</span>
                    <input
                      id={`preset-${activeRole}-p-pack`}
                      type="number"
                      min={0}
                      max={1_000_000}
                      step={0.01}
                      className={inputNum}
                      value={d.price_pack_usd}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [activeRole]: {
                            ...prev[activeRole],
                            price_pack_usd: parseFloat(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </EditableMetricRow>
                  <div className="mt-1 space-y-2">
                    <EditableMetricRow label="Included hours" suffix="h">
                      <input
                        id={`preset-${activeRole}-pack`}
                        type="number"
                        min={1}
                        max={10000}
                        step={1}
                        className={inputNum}
                        value={d.monthly_pack_hours}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [activeRole]: {
                              ...prev[activeRole],
                              monthly_pack_hours: parseInt(e.target.value, 10) || 0,
                            },
                          }))
                        }
                      />
                    </EditableMetricRow>
                    <EditableMetricRow label="Max add-on hours" suffix="h">
                      <input
                        id={`preset-${activeRole}-extra-cap`}
                        type="number"
                        min={0}
                        max={10000}
                        step={1}
                        className={inputNum}
                        value={d.max_monthly_extra_hours}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [activeRole]: {
                              ...prev[activeRole],
                              max_monthly_extra_hours: parseInt(e.target.value, 10) || 0,
                            },
                          }))
                        }
                      />
                    </EditableMetricRow>
                    <EditableMetricRow label="Extra hour rate" suffix="$/hr">
                      <span className="shrink-0 text-sm text-gray-400">$</span>
                      <input
                        id={`preset-${activeRole}-p-extra`}
                        type="number"
                        min={0}
                        max={1_000_000}
                        step={0.0001}
                        className={inputNum}
                        value={d.price_extra_per_hour_usd}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [activeRole]: {
                              ...prev[activeRole],
                              price_extra_per_hour_usd: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                      />
                    </EditableMetricRow>
                  </div>
                </PlanCardShell>

                {/* Unlimited */}
                <PlanCardShell gradientClass="from-amber-400 via-amber-500 to-orange-500">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-900 ring-1 ring-amber-100 shadow-sm">
                    <i className="ri-vip-crown-fill text-[13px]" aria-hidden /> Premium
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">Unlimited</h3>
                    <p className="mt-1 text-xs text-gray-500">No cap · max performance</p>
                  </div>
                  <EditableMetricRow label="List price" suffix="/ mo">
                    <span className="shrink-0 text-sm text-gray-400">$</span>
                    <input
                      id={`preset-${activeRole}-p-unl`}
                      type="number"
                      min={0}
                      max={1_000_000}
                      step={0.01}
                      className={inputNum}
                      value={d.price_unlimited_usd}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [activeRole]: {
                            ...prev[activeRole],
                            price_unlimited_usd: parseFloat(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </EditableMetricRow>
                  <div className="mt-auto pt-2">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      Monthly hour cap
                    </div>
                    <div className="rounded-2xl bg-amber-50/90 py-2.5 text-center text-sm font-semibold text-amber-900 ring-1 ring-amber-100/90 shadow-sm">
                      ∞ Unlimited
                    </div>
                  </div>
                </PlanCardShell>
              </div>

              <div className="mx-4 mb-6 sm:mx-8">
                <div className="rounded-2xl bg-white px-5 py-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8)] ring-1 ring-gray-950/[0.06] sm:px-7">
                  <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700/90">
                    Analysis time — enforced in app
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
                    <div className="rounded-xl bg-gradient-to-br from-emerald-50/90 to-white px-5 py-4 text-center shadow-sm ring-1 ring-emerald-100/80">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Daily mode limit
                      </div>
                      <div className="flex flex-wrap items-baseline justify-center gap-2">
                        <span className="text-3xl font-bold tabular-nums tracking-tight text-emerald-800">{d.daily_hours}</span>
                        <span className="text-xs font-medium text-gray-500">h / day</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-emerald-50/90 to-white px-5 py-4 text-center shadow-sm ring-1 ring-emerald-100/80">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Monthly pack base
                      </div>
                      <div className="flex flex-wrap items-baseline justify-center gap-2">
                        <span className="text-3xl font-bold tabular-nums tracking-tight text-emerald-800">
                          {d.monthly_pack_hours}
                        </span>
                        <span className="text-xs font-medium text-gray-500">h / mo</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-emerald-50/90 to-white px-5 py-4 text-center shadow-sm ring-1 ring-emerald-100/80">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Max add-on cap
                      </div>
                      <div className="flex flex-wrap items-baseline justify-center gap-2">
                        <span className="text-3xl font-bold tabular-nums tracking-tight text-emerald-800">
                          {d.max_monthly_extra_hours}
                        </span>
                        <span className="text-xs font-medium text-gray-500">h max</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100/90 bg-gradient-to-b from-white to-gray-50/90 px-4 py-5 sm:px-8">
                <p className="max-w-xl text-xs text-gray-500">
                  Save applies only to <strong className="text-gray-800">{tabLabel}</strong>. Switch tab to edit another
                  segment.
                </p>
                <button
                  type="button"
                  disabled={savingRole === activeRole}
                  onClick={() => void saveRole(activeRole)}
                  className="shrink-0 rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-600/25 transition hover:bg-emerald-700 disabled:opacity-50"
                >
                  {savingRole === activeRole ? 'Saving…' : `Save ${tabLabel} rules`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
